import { Link } from "react-router-dom";
import { Phone, ShieldCheck, Clock, BadgeDollarSign, Award, ArrowRight } from "lucide-react";
import { SERVICES, FAQS, PHONE, PHONE_HREF, BOOKING_URL, IMAGES } from "@/data/site";
import { ServiceCard } from "@/components/ServiceCard";
import { LeadForm } from "@/components/LeadForm";
import { EstimateForm } from "@/components/EstimateForm";
import { PayLaterBanner } from "@/components/PayLaterBanner";
import { PayLaterMessage } from "@/components/PayLaterMessage";
import { FinancingCallout } from "@/components/FinancingCallout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TRUST = [
  { icon: Award, title: "Since 1998", text: "Over 25 years keeping South Florida cool." },
  { icon: ShieldCheck, title: "Licensed & Insured Contractors", text: "State-licensed and insured HVAC contractors on every job." },
  { icon: BadgeDollarSign, title: "Upfront Flat Pricing", text: "The price we quote is the price you pay." },
  { icon: Clock, title: "24/7 Emergency Booking", text: "Call any time — our system takes your request, and a technician is dispatched after a $200 prepaid emergency booking fee, which is applied toward your repair." },
];

export const HomeHero = () => (
  <section className="relative">
    <img src={IMAGES.hero} alt="Modern South Florida home with efficient air conditioning" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-black/50" />
    <div className="relative max-w-4xl mx-auto px-4 py-16 sm:py-24 lg:py-28 text-center">
      <img src="/logo-horizontal.webp" alt="Central Air Depot — AC Service, Install, Maintenance" data-testid="hero-logo"
        className="h-24 sm:h-32 lg:h-36 w-auto mx-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.55)]" />
      <p data-testid="hero-slogan" className="font-heading italic font-bold text-lg sm:text-2xl text-brand-ice mt-5 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
        Let us upgrade the air you breathe.
      </p>
      <p data-testid="hero-commercial-residential" className="text-sm sm:text-base tracking-[0.25em] uppercase font-bold text-white mt-2 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
        Commercial &amp; Residential
      </p>
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-300 mt-10 mb-4">South Florida's AC Experts Since 1998</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-black text-white">
        Cool comfort, handled. Honest AC service with upfront prices.
      </h1>
      <p className="text-base sm:text-lg text-zinc-100 mt-6 max-w-xl mx-auto leading-relaxed">
        Duct cleaning from $67 per vent, $99 diagnostics waived with repair, and maintenance plans from $24.99 a month. Licensed &amp; insured contractors, with 24/7 emergency booking.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
        <Link to="/contact" data-testid="hero-get-help-btn"
          className="bg-brand-red hover:bg-[#B02222] text-white font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
          Get AC Help
        </Link>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-book-service-btn"
          className="bg-brand-ice hover:bg-[#7FA8F0] text-brand-ink font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
          Book Service
        </a>
        <Link to="/maintenance-plans" data-testid="hero-join-plan-btn"
          className="border-2 border-white text-white hover:bg-white hover:text-brand-ink font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
          Join a Maintenance Plan
        </Link>
      </div>
    </div>
  </section>
);

export const TrustStrip = () => (
  <section className="bg-white border-b border-zinc-200">
    <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {TRUST.map((t) => (
        <div key={t.title} data-testid={`trust-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-start gap-3">
          <t.icon className="w-8 h-8 text-brand shrink-0" />
          <div>
            <p className="font-heading font-bold text-brand-ink">{t.title}</p>
            <p className="text-sm text-zinc-600">{t.text}</p>
          </div>
        </div>
      ))}
    </div>
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <FinancingCallout variant="residential" />
    </div>
  </section>
);

export const FeaturedOffers = () => (
  <section className="bg-brand-subtle">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">Transparent Pricing</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-brand-ink">Featured offers, no surprises</h2>
        </div>
        <Link to="/services" data-testid="offers-view-all-link" className="text-brand font-medium flex items-center gap-1 hover:gap-2 transition-[gap] duration-200">
          All services <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.slice(0, 6).map((s) => (
          <ServiceCard key={s.id} service={s} highlight={s.id === "maintenance-plan"} />
        ))}
      </div>
    </div>
  </section>
);

export const MaintenanceBanner = () => (
  <section className="bg-brand-ink text-white">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-ice mb-3">Central Air Depot Maintenance Plan</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black leading-tight">
          Two tune-ups a year. Zero mid-summer breakdowns.
        </h2>
        <p className="text-zinc-300 mt-4 leading-relaxed max-w-lg">
          Join the Annual Residential Maintenance Plan and get two seasonal tune-ups plus a free non-emergency diagnostic service call — all for less than a dollar a day.
        </p>
        <div className="flex items-baseline gap-6 mt-6">
          <div><span className="font-heading font-black text-4xl text-brand-ice">$249</span><span className="text-zinc-400 text-sm"> /year</span></div>
          <div className="text-zinc-500">or</div>
          <div><span className="font-heading font-black text-4xl text-brand-ice">$24.99</span><span className="text-zinc-400 text-sm"> /month</span></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-banner-join-btn"
            className="bg-brand-ice hover:bg-[#7FA8F0] text-brand-ink font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
            Join the Plan
          </a>
          <Link to="/maintenance-plans" data-testid="plan-banner-details-btn"
            className="border-2 border-zinc-600 hover:border-white text-white font-bold px-8 py-4 rounded-sm text-center transition-colors duration-200">
            See What's Included
          </Link>
        </div>
      </div>
      <img src={IMAGES.repair} alt="HVAC technician performing seasonal AC tune-up" className="w-full h-72 lg:h-96 object-cover rounded-sm" />
    </div>
  </section>
);

const ESTIMATE_POINTS = [
  "Free, upfront estimates on new equipment",
  "Financing available with approved credit",
  "Extended warranties on parts, equipment & labor",
  "Leading national brands, installed by licensed pros",
];

export const EstimateSection = () => (
  <section id="estimate" className="bg-white border-y border-zinc-200">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">Free Replacement Estimates</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-brand-ink">Time for a new system?</h2>
        <p className="text-zinc-600 mt-4 leading-relaxed">
          Snap two quick photos — your existing unit and its nameplate — tell us your home size, and we'll call you with a free, honest replacement estimate. No site visit needed to get started.
        </p>
        <ul className="mt-6 space-y-3">
          {ESTIMATE_POINTS.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-zinc-700">
              <ShieldCheck className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-6">
        <EstimateForm />
        <PayLaterMessage />
        <PayLaterBanner />
      </div>
    </div>
  </section>
);

export const FaqLeadSection = () => (
  <section className="bg-brand-subtle">
    <div className="max-w-7xl mx-auto px-4 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">Common Questions</p>
        <h2 className="text-2xl sm:text-3xl tracking-tight font-black text-brand-ink mb-6">Straight answers, no runaround</h2>
        <Accordion type="single" collapsible data-testid="home-faq-accordion">
          {FAQS.slice(0, 4).map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`}>
              <AccordionTrigger data-testid={`home-faq-trigger-${i}`} className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-zinc-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <Link to="/faqs" data-testid="home-faq-more-link" className="text-brand font-medium flex items-center gap-1 mt-4 hover:gap-2 transition-[gap] duration-200">
          All FAQs <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <LeadForm />
    </div>
  </section>
);

export const FinalCta = () => (
  <section className="bg-brand text-white">
    <div className="max-w-7xl mx-auto px-4 py-16 flex flex-col lg:flex-row items-center justify-between gap-8">
      <div>
        <h2 className="text-2xl sm:text-3xl tracking-tight font-black">AC acting up? Don't sweat it.</h2>
        <p className="text-blue-100 mt-2">Call now or book online in under a minute.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <a href={PHONE_HREF} data-testid="cta-call-btn" className="bg-white text-brand font-bold px-8 py-4 rounded-sm flex items-center justify-center gap-2 hover:bg-brand-subtle transition-colors duration-200">
          <Phone className="w-5 h-5" /> {PHONE}
        </a>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-book-btn"
          className="bg-brand-ice text-brand-ink font-bold px-8 py-4 rounded-sm text-center hover:bg-[#7FA8F0] transition-colors duration-200">
          Book Service Online
        </a>
      </div>
    </div>
  </section>
);
